<template>
    <div class="h-full">
        <Card class="h-full">
            <template #title>
                <div class="flex items-center gap-2">
                    <Icon
                        name="box"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span class="truncate">
                        {{ service.title }}
                    </span>
                </div>
            </template>
            <template #content>
                <div class="border-b border-b-gray-200 mb-4"></div>

                <div>
                    <div class="text-sm flex gap-4 flex-wrap">
                        <div class="flex items-center gap-1">
                            <Icon
                                name="stopwatch"
                                class="w-3.5 h-3.5 text-gray-500"
                            />
                            <span> {{ service.durationInMinutes }} mins </span>
                        </div>
                        <div
                            v-if="service.shouldDisplayMultipleAttendeeIcon()"
                            class="flex items-center gap-1"
                        >
                            <Icon
                                name="users"
                                class="w-4 h-4 text-gray-500"
                            />
                            <span> Group </span>
                        </div>
                        <div
                            v-if="service.shouldDisplayRecurringIcon()"
                            class="flex items-center gap-1"
                        >
                            <Icon
                                name="arrow-rotate"
                                class="w-4 h-4 text-gray-500"
                            />
                            <span> Recurring </span>
                        </div>
                        <div
                            v-if="service.shouldDisplayPaidIcon()"
                            class="flex items-center gap-1"
                        >
                            <Icon
                                name="dollar-sign"
                                class="w-4 h-4 text-gray-500"
                            />
                            <span> Paid </span>
                        </div>
                        <div
                            v-if="service.shouldDisplayDisabledIcon()"
                            class="flex items-center gap-1"
                        >
                            <Icon
                                name="locker"
                                class="w-3.5 h-3.5 text-gray-500"
                            />
                            <span> Disabled </span>
                        </div>
                        <div
                            v-if="service.shouldDisplayInvisibleIcon()"
                            class="flex items-center gap-1"
                        >
                            <Icon
                                name="eye-closed"
                                class="w-4 h-4 text-gray-500"
                            />
                            <span> Hidden </span>
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <div class="flex flex-col w-full gap-1">
                    <ShareLinkButtonModal
                        buttonSize="small"
                        :analyticsSurface="
                            AnalyticsEventSurfaceEnum.Share_ServicePageLink_ServiceCard
                        "
                        :analyticsServiceId="service.id"
                        :shareUrl="
                            urlFactory.makeServiceBookingFormPageUrl(
                                company.slug,
                                service.slug,
                            )
                        "
                    />
                    <Button
                        as="a"
                        :href="
                            urlFactory.makeServiceBookingFormPageUrl(
                                company.slug,
                                service.slug,
                            )
                        "
                        color="light"
                        size="small"
                        iconName="external-link"
                        target="_blank"
                    >
                        View
                    </Button>
                    <Button
                        v-if="showEditService"
                        as="a"
                        :href="urlFactory.makeServiceEditPageUrl(service.id)"
                        color="light"
                        size="small"
                        iconName="edit"
                    >
                        Edit
                    </Button>
                    <DeleteServiceButtonModal
                        v-if="showDeleteService"
                        :service="service"
                        @onSuccess="handleServiceDeleted()"
                    />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import Card from '@/Shared/Component/Card.vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import type { PropType } from 'vue'
    import Service from '@/App/Service/Entity/Service'
    import Company from '@/App/Company/Entity/Company'
    import Button from '@/Shared/Component/Button.vue'
    import ShareLinkButtonModal from '@/App/Service/Component/Service/ShareLinkButtonModal.vue'
    import DeleteServiceButtonModal from '@/App/Service/Component/Service/DeleteServiceButtonModal.vue'
    import { AnalyticsEventSurfaceEnum } from '@/Shared/AutoGenerated/Main'

    const emit = defineEmits<{
        onServiceDeleted: [{ service: Service }]
    }>()

    const props = defineProps({
        service: {
            type: Object as PropType<Service>,
            required: true,
        },
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        showEditService: {
            type: Boolean,
            required: true,
        },
        showDeleteService: {
            type: Boolean,
            required: true,
        },
    })

    const handleServiceDeleted = () => {
        emit('onServiceDeleted', { service: props.service })
    }
    const urlFactory = new UrlFactory()
</script>
